import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Home, CreditCard, HelpCircle } from "lucide-react";
import Upgrade from "./Upgrade";
import BulkProductUpdate from "./BulkProductUpdate";
import Help from "./Help";
import { getStoreId, getSettings } from "../utils/storage";
import * as logger from "../utils/logger";
import "./MainApp.css";

const MainApp = ({ storeInfo, onLogout }) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('bulk-product-update');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [hasAutoConnected, setHasAutoConnected] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Sync state from URL hash on load and when user changes hash (e.g. back button)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '').toLowerCase();
      if (hash && ['bulk-product-update', 'upgrade', 'help'].includes(hash)) {
        setActiveTab(hash);
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Sync URL hash when activeTab changes (so bookmarking / back works)
  useEffect(() => {
    const want = activeTab;
    const current = (window.location.hash || '').replace('#', '');
    if (current !== want) {
      window.location.hash = want;
    }
  }, [activeTab]);

  // Ecwid iframe: single fixed height, no dynamic resize. Iframe is always this tall; content scrolls inside if needed.
  useEffect(() => {
    if (typeof window.EcwidApp === 'undefined' || window.self === window.top) return;
    const FIXED_IFRAME_HEIGHT = 1400;
    window.EcwidApp.setSize({ height: FIXED_IFRAME_HEIGHT });
  }, [activeTab]);

  // Load settings (skip fetch if already in storage from App's loadApiKeyAndSettings)
  useEffect(() => {
    const loadSettings = async () => {
      const storeId = getStoreId();
      logger.log('=== MainApp: loadSettings called ===');
      logger.log('MainApp: Store ID from getStoreId():', storeId);

      if (!storeId) {
        logger.log('MainApp: No store ID found in sessionStorage');
        setIsLoadingSettings(false);
        setSettings({});
        return;
      }

      const stored = getSettings();
      logger.log('MainApp: Settings from getSettings():', stored);
      if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) {
        setSettings(stored);
        setIsLoadingSettings(false);
        return;
      }
      logger.log('MainApp: No stored settings found, fetching from backend...');
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      try {
        const response = await fetch(`${apiUrl}/api/settings?id=${storeId}`);
        logger.log('MainApp: Settings response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          logger.log('MainApp: Settings data received:', { 
            success: data.success, 
            hasSettings: !!data.settings,
            hasApiKey: !!data.settings?.apiKey,
            settingsKeys: data.settings ? Object.keys(data.settings) : []
          });
          
          if (data.success && data.settings) {
            setSettings(data.settings);
          } else {
            // Set empty object to indicate settings were loaded but no API key exists
            setSettings({});
          }
        } else {
          logger.warn('MainApp: Settings response not ok:', response.status);
          // Set empty object to indicate settings were attempted but failed
          setSettings({});
        }
      } catch (error) {
        logger.error('MainApp: Failed to load settings:', error);
        setSettings({});
      } finally {
        setIsLoadingSettings(false);
      }
    };
    
    loadSettings();
  }, []);

  // Auto-connect user when page loads without API key (similar to BigCommerce)
  useEffect(() => {
    logger.log('=== MainApp: Auto-connect check ===');
    logger.log('MainApp: Auto-connect conditions:', {
      isLoadingSettings,
      settings: settings ? 'exists' : 'null',
      hasApiKey: settings?.apiKey ? 'yes' : 'no',
      hasAutoConnected,
      isSaving
    });
    
    // Only auto-connect once, when settings are loaded and no API key exists
    // Match BigCommerce condition exactly: !isLoadingSettings && settings && !settings.apiKey && !hasAutoConnected && !isSaving
    // Note: settings can be {} (empty object) which is truthy, or null/undefined which is falsy
    if (!isLoadingSettings && settings && !settings.apiKey && !hasAutoConnected && !isSaving) {
      logger.log('MainApp: ✅ Auto-connect condition met, starting auto-connect...');
      setHasAutoConnected(true);
      const storeId = getStoreId(); // Use utility function
      logger.log('MainApp: Store ID for auto-connect:', storeId);
      
      if (!storeId) {
        logger.error('MainApp: ❌ No store ID found for auto-connect');
        setHasAutoConnected(false);
        return;
      }
      
      // Use setTimeout to avoid calling during render (matching BigCommerce)
      setTimeout(() => {
        const apiUrl = import.meta.env.VITE_BACKEND_URL;
        const registerUrl = `${apiUrl}/api/register?id=${storeId}`;
        logger.log('MainApp: 📞 Calling register endpoint:', registerUrl);
        
        fetch(registerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })
        .then(res => {
          logger.log('MainApp: Register response status:', res.status);
          if (!res.ok) {
            return res.json().then(err => {
              throw new Error(err.error || err.message || `HTTP ${res.status}`);
            });
          }
          return res.json();
        })
        .then(data => {
          logger.log('MainApp: ✅ Auto-connect response:', data);
          if (data.success || data.api_key) {
            // Reload the page to get fresh settings with the new API key
            logger.log('MainApp: ✅ Auto-connect successful, reloading page...');
            window.location.reload();
          } else {
            logger.warn('MainApp: ⚠️ Auto-connect response missing success/api_key:', data);
            setHasAutoConnected(false); // Reset on failure
          }
        })
        .catch(err => {
          logger.error('MainApp: ❌ Auto-connect failed:', err);
          setHasAutoConnected(false); // Reset on error
        });
      }, 100);
    } else {
      logger.log('MainApp: ⏸️ Auto-connect skipped - conditions not met');
    }
  }, [settings, isLoadingSettings, hasAutoConnected, isSaving]);

  // Reset hasAutoConnected once API key is successfully loaded
  useEffect(() => {
    if (hasAutoConnected && settings?.apiKey) {
      logger.log('MainApp: API key loaded, resetting hasAutoConnected');
      setHasAutoConnected(false);
    }
  }, [hasAutoConnected, settings?.apiKey]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const showContentLoading = isLoadingSettings || (hasAutoConnected && !settings?.apiKey);

  const renderTabContent = () => {
    if (showContentLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="text-center">
            <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
              <span className="visually-hidden">{t("mainApp.loading")}</span>
            </div>
            <p className="text-muted small mb-0">
              {hasAutoConnected ? t("mainApp.connectingAccount") : t("mainApp.loadingSettings")}
            </p>
          </div>
        </div>
      );
    }
    switch (activeTab) {
      case 'bulk-product-update':
        return <BulkProductUpdate />;
      case 'upgrade':
        return <Upgrade />;
      case 'help':
        return <Help />;
      default:
        return <BulkProductUpdate />;
    }
  };

  return (
    <div className="main-app h-100 overflow-hidden" data-main-layout="true" data-active-tab={activeTab}>
      <div className="main-app-layout d-flex flex-column h-100" style={{ backgroundColor: '#f8f9fa' }}>
        {/* Top bar: logo + app name left; Home, Upgrade, Help, Language right */}
        <header className="main-app-topbar">
          <div className="topbar-brand">
            <img
              src="/img/bulkeditpro-long.webp"
              alt={t("mainApp.logoAlt")}
              className="topbar-logo"
            />
          </div>
          <nav className="topbar-nav-center" role="navigation">
            <button
              type="button"
              className={`topbar-nav-btn topbar-nav-btn-home ${activeTab === 'bulk-product-update' ? 'active' : ''}`}
              onClick={() => handleTabClick('bulk-product-update')}
            >
              <Home size={18} className="me-2" aria-hidden />
              {t("mainApp.nav.home")}
            </button>
            <button
              type="button"
              className={`topbar-nav-link ${activeTab === 'upgrade' ? 'active' : ''}`}
              onClick={() => handleTabClick('upgrade')}
            >
              <CreditCard size={14} className="me-1" aria-hidden />
              {t("mainApp.nav.upgrade")}
            </button>
            <button
              type="button"
              className={`topbar-nav-link ${activeTab === 'help' ? 'active' : ''}`}
              onClick={() => handleTabClick('help')}
            >
              <HelpCircle size={14} className="me-1" aria-hidden />
              {t("mainApp.nav.help")}
            </button>
          </nav>
          <div className="topbar-right">
            <div className="topbar-language" role="group" aria-label={t("mainApp.language")}>
              {[
                { code: 'en', label: 'EN' },
                { code: 'fr', label: 'FR' },
                { code: 'es', label: 'ES' }
              ].map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  className={`topbar-lang-btn ${i18n.language === code ? 'active' : ''}`}
                  onClick={() => i18n.changeLanguage(code)}
                  title={code === 'en' ? 'English' : code === 'fr' ? 'Français' : 'Español'}
                  aria-pressed={i18n.language === code}
                  aria-label={code === 'en' ? 'English' : code === 'fr' ? 'Français' : 'Español'}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </header>

        {/* Main content */}
        <div className="main-content-wrapper flex-grow-1">
          <main className="main-content">
            {renderTabContent()}
          </main>
        </div>
      </div>
    </div>
  );
};

export default MainApp; 